import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Mountain, AlertTriangle, Search, Upload, Wand2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PinPickerMap } from "@/components/PinPickerMap";

export const Route = createFileRoute("/admin/puertos")({
  head: () => ({
    meta: [
      { title: "Administrar puertos — Watts" },
      { name: "description", content: "Gestión del catálogo de puertos, coeficientes APM y puntos de inicio de Watts." },
      { property: "og:title", content: "Administrar puertos — Watts" },
      { property: "og:description", content: "Gestión del catálogo de puertos, coeficientes APM y puntos de inicio de Watts." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminPorts,
});

interface Port {
  id: string;
  name: string;
  slope_side: string | null;
  province: string;
  comarca: string | null;
  altitude_m: number | null;
  length_km: number | null;
  elevation_gain_m: number | null;
  avg_gradient: number | null;
  max_gradient: number | null;
  apm_coefficient: number | null;
  difficulty: number | null;
  category: string | null;
  surface: string;
  profile_url: string | null;
  watts_reward: number | null;
  is_active: boolean | null;
  lat: number | null;
  lng: number | null;
  start_municipality: string | null;
  start_lat: number | null;
  start_lng: number | null;
  start_address: string | null;
  is_departure_point: boolean;
}

const CATEGORIES = ["HC", "1", "2", "3", "4"];

// Categorización Strava: length_km * 1000 * avg_gradient
function stravaCategory(lengthKm: number | null, avgGradient: number | null) {
  const score = (Number(lengthKm) || 0) * 1000 * (Number(avgGradient) || 0);
  const category = score >= 80000 ? "HC" : score >= 64000 ? "1" : score >= 32000 ? "2" : score >= 16000 ? "3" : "4";
  const map: Record<string, { difficulty: number; watts_reward: number }> = {
    HC: { difficulty: 5, watts_reward: 8 },
    "1": { difficulty: 4, watts_reward: 5 },
    "2": { difficulty: 3, watts_reward: 3 },
    "3": { difficulty: 2, watts_reward: 2 },
    "4": { difficulty: 1, watts_reward: 1 },
  };
  return { category, ...map[category] };
}
const SURFACES = ["asfalto", "gravel", "mixto"];

// Pines conservados en la corrección APM: el aviso desaparece al ajustar sus coordenadas.
const PINS_TO_REVIEW: Record<string, readonly [number, number]> = {
  "0e1da5ad-42a9-4413-a8d1-9f78c50c1a9e": [42.2036044, 1.818818],
  "1b719054-6caf-48b9-8cce-5a4b77d2002a": [41.2358351, 1.8119895],
  "1ed6a3cd-cd33-4884-b3ec-39df897a08bc": [42.2372601, 2.7097379],
  "7bd41881-c2fa-4d2c-8086-a78b7744230f": [41.2281084, 1.0336452],
  "89f6f60c-a3a6-45ec-acd1-9eee3e4d4a29": [41.1457974, 0.9305023],
};

const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));
const fmt = (n: number | null | undefined, d = 1) =>
  n === null || n === undefined ? "—" : Number(n).toFixed(d).replace(".", ",");

function Stars({ n }: { n: number | null }) {
  return (
    <span className="text-[11px] tracking-tight text-[#F5C518]">
      {"★".repeat(n ?? 0)}
      <span className="text-white/20">{"★".repeat(Math.max(0, 5 - (n ?? 0)))}</span>
    </span>
  );
}

async function geocode(query: string) {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=es&q=${encodeURIComponent(query)}`,
    { headers: { "Accept-Language": "es" } },
  );
  const data = await res.json();
  if (!Array.isArray(data) || data.length === 0) return null;
  return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), address: data[0].display_name as string };
}

function AdminPorts() {
  const [rows, setRows] = useState<Port[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fProvince, setFProvince] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fSurface, setFSurface] = useState("");
  const [fNoCoords, setFNoCoords] = useState(false);
  const [sort, setSort] = useState<"name" | "gain" | "length" | "apm">("name");
  const [editing, setEditing] = useState<Port | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Port | null>(null);
  const [importing, setImporting] = useState(false);
  const [filling, setFilling] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("ports").select("*").order("name");
    if (error) toast.error(error.message);
    setRows((data as unknown as Port[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const provinces = useMemo(
    () => Array.from(new Set(rows.map((r) => r.province).filter(Boolean))).sort(),
    [rows],
  );

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (term && !`${r.name} ${r.start_municipality ?? ""}`.toLowerCase().includes(term)) return false;
      if (fProvince && r.province !== fProvince) return false;
      if (fCategory && r.category !== fCategory) return false;
      if (fSurface && r.surface !== fSurface) return false;
      if (fNoCoords && r.start_lat != null && r.start_lng != null) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sort === "gain") return (b.elevation_gain_m ?? 0) - (a.elevation_gain_m ?? 0);
      if (sort === "length") return (b.length_km ?? 0) - (a.length_km ?? 0);
      if (sort === "apm") return (b.apm_coefficient ?? -1) - (a.apm_coefficient ?? -1);
      return a.name.localeCompare(b.name, "es");
    });
    return list;
  }, [rows, q, fProvince, fCategory, fSurface, fNoCoords, sort]);

  const toggleActive = async (r: Port) => {
    const { error } = await supabase.from("ports").update({ is_active: !r.is_active }).eq("id", r.id);
    if (error) toast.error(error.message);
    else { toast.success(r.is_active ? "Puerto desactivado" : "Puerto activado"); load(); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const { error } = await supabase.from("ports").delete().eq("id", deleting.id);
    if (error) toast.error(error.message);
    else { toast.success("Puerto eliminado"); load(); }
    setDeleting(null);
  };

  const fillMissing = async () => {
    const pending = rows.filter((r) => r.start_lat == null || r.start_lng == null);
    if (pending.length === 0) { toast.info("Todos los puertos tienen coordenadas de inicio."); return; }
    setFilling(true);
    let ok = 0, ko = 0;
    for (const p of pending) {
      const query = [p.start_municipality, p.province, "España"].filter(Boolean).join(", ");
      try {
        const hit = p.start_municipality ? await geocode(query) : null;
        if (hit) {
          const { error } = await supabase
            .from("ports")
            .update({ start_lat: hit.lat, start_lng: hit.lng, start_address: hit.address })
            .eq("id", p.id);
          if (error) ko++; else ok++;
        } else ko++;
      } catch { ko++; }
      // Nominatim: máximo 1 petición por segundo
      await new Promise((r) => setTimeout(r, 1100));
    }
    setFilling(false);
    toast.success(`${ok} rellenados · ${ko} no encontrados`);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">Puertos</h1>
          <p className="text-sm text-muted-foreground">Catálogo de puertos y sus puntos de inicio.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={fillMissing} disabled={filling}
            className="flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-4 py-2 text-sm disabled:opacity-50">
            <Wand2 className="h-4 w-4" /> {filling ? "Rellenando…" : "Rellenar inicios que falten"}
          </button>
          <button onClick={() => setImporting(true)}
            className="flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-4 py-2 text-sm">
            <Upload className="h-4 w-4" /> Importar puertos
          </button>
          <button onClick={() => setCreating(true)}
            className="press-scale flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
            <Plus className="h-4 w-4" /> Añadir puerto
          </button>
        </div>
      </div>

      {/* Buscador + filtros */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o municipio…"
            className="w-full rounded-xl border border-border bg-surface py-2.5 pl-9 pr-3 text-sm" />
        </div>
        <select value={fProvince} onChange={(e) => setFProvince(e.target.value)}
          className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm">
          <option value="">Provincia</option>
          {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)}
          className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm">
          <option value="">Categoría</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={fSurface} onChange={(e) => setFSurface(e.target.value)}
          className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm">
          <option value="">Superficie</option>
          {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as any)}
          className="rounded-xl border border-border bg-surface px-3 py-2.5 text-sm">
          <option value="name">Orden: A-Z</option>
          <option value="gain">Orden: desnivel</option>
          <option value="length">Orden: longitud</option>
          <option value="apm">Orden: coeficiente APM</option>
        </select>
        <button onClick={() => setFNoCoords((v) => !v)}
          className={`rounded-full border px-3 py-2 text-xs ${fNoCoords ? "border-red-500/40 bg-red-500/15 text-red-300" : "border-border bg-surface-elevated text-white/70"}`}>
          Sin coordenadas de inicio
        </button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-surface">
        <div className="min-w-[1100px]">
          <div className="grid grid-cols-[1.5fr_0.8fr_0.5fr_1fr_1.3fr_1fr_0.6fr_0.6fr_1.2fr] gap-3 border-b border-border bg-[#0f0f0f] px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            <div>Nombre</div><div>Categoría</div><div>APM</div><div>Provincia</div><div>Datos</div>
            <div>Inicio</div><div>Superficie</div><div>Estado</div><div className="text-right">Acciones</div>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Cargando…</div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Sin puertos.</div>
          ) : visible.map((r) => {
            const noCoords = r.start_lat == null || r.start_lng == null;
            const previousPin = PINS_TO_REVIEW[r.id];
            const needsPinReview = previousPin && r.start_lat === previousPin[0] && r.start_lng === previousPin[1];
            return (
              <div key={r.id} className="grid grid-cols-[1.5fr_0.8fr_0.5fr_1fr_1.3fr_1fr_0.6fr_0.6fr_1.2fr] items-center gap-3 border-b border-border px-4 py-3 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 truncate font-medium">
                    <Mountain className="h-3.5 w-3.5 shrink-0 text-primary" /> {r.name}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{r.slope_side || "—"}</div>
                </div>
                <div>
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold">{r.category || "—"}</span>
                  <div className="mt-0.5"><Stars n={r.difficulty} /></div>
                </div>
                <div className="text-xs tabular-nums text-foreground">{r.apm_coefficient ?? "—"}</div>
                <div className="text-xs">
                  {r.province}
                  <div className="text-muted-foreground">{r.comarca || "—"}</div>
                </div>
                <div className="text-xs text-white/80">
                  {fmt(r.length_km, 2)} km · {r.elevation_gain_m ?? "—"} m D+ · {fmt(r.avg_gradient, 2)}%
                </div>
                <div className="text-xs">
                  {r.start_municipality || "—"}
                  {needsPinReview && (
                    <div className="mt-1 flex items-start gap-1 text-primary" title="Vertiente actualizada; el pin anterior se ha conservado para revisión manual.">
                      <AlertTriangle className="h-3 w-3 shrink-0" /> Revisar pin
                    </div>
                  )}
                  {noCoords && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">
                      <AlertTriangle className="h-3 w-3" /> sin coords
                    </div>
                  )}
                </div>
                <div className="text-xs">{r.surface}</div>
                <div>
                  {r.is_active ? (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] text-emerald-400">Activo</span>
                  ) : (
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/60">Inactivo</span>
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-1.5">
                  <button onClick={() => setEditing(r)}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-elevated px-3 py-1 text-xs hover:bg-white/5">
                    <Pencil className="h-3 w-3" /> Editar
                  </button>
                  <button onClick={() => toggleActive(r)}
                    className="rounded-full border border-border bg-surface-elevated px-3 py-1 text-xs hover:bg-white/5">
                    {r.is_active ? "Desactivar" : "Activar"}
                  </button>
                  <button onClick={() => setDeleting(r)}
                    className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs text-red-400 hover:bg-red-500/20">
                    <Trash2 className="h-3 w-3" /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(creating || editing) && (
        <PortDialog port={editing ?? undefined} onClose={() => { setCreating(false); setEditing(null); load(); }} />
      )}
      {importing && <ImportDialog onClose={() => { setImporting(false); load(); }} existing={rows} />}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setDeleting(null)}>
          <div onClick={(e) => e.stopPropagation()} className="mx-4 w-full max-w-sm rounded-3xl border border-border bg-[#1a1a1a] p-5">
            <h3 className="font-display text-xl">Eliminar puerto</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              ¿Seguro que quieres eliminar <span className="text-white">{deleting.name}</span>? No se puede deshacer.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleting(null)} className="rounded-full border border-border bg-surface-elevated px-4 py-2 text-sm">Cancelar</button>
              <button onClick={confirmDelete} className="rounded-full bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-600">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm";
const labelCls = "text-[11px] uppercase tracking-wider text-muted-foreground";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className={labelCls}>{label}</label><div className="mt-1">{children}</div></div>;
}

function PortDialog({ port, onClose }: { port?: Port; onClose: () => void }) {
  const isEdit = !!port;
  const [f, setF] = useState({
    name: port?.name ?? "",
    slope_side: port?.slope_side ?? "",
    province: port?.province ?? "",
    comarca: port?.comarca ?? "",
    altitude_m: port?.altitude_m?.toString() ?? "",
    length_km: port?.length_km?.toString() ?? "",
    elevation_gain_m: port?.elevation_gain_m?.toString() ?? "",
    avg_gradient: port?.avg_gradient?.toString() ?? "",
    max_gradient: port?.max_gradient?.toString() ?? "",
    difficulty: port?.difficulty?.toString() ?? "3",
    category: port?.category ?? "2",
    surface: port?.surface ?? "asfalto",
    profile_url: port?.profile_url ?? "",
    watts_reward: port?.watts_reward?.toString() ?? "10",
    is_active: port?.is_active ?? true,
    start_municipality: port?.start_municipality ?? "",
    start_address: port?.start_address ?? "",
    is_departure_point: port?.is_departure_point ?? true,
    lat: port?.lat?.toString() ?? "",
    lng: port?.lng?.toString() ?? "",
  });
  const [startLat, setStartLat] = useState<number | null>(port?.start_lat ?? null);
  const [startLng, setStartLng] = useState<number | null>(port?.start_lng ?? null);
  const [gradientTouched, setGradientTouched] = useState(!!port?.avg_gradient);
  const [saving, setSaving] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);

  const set = (k: string, v: any) => setF((p) => ({ ...p, [k]: v }));

  // avg_gradient autocalculado mientras el admin no lo sobrescriba
  useEffect(() => {
    if (gradientTouched) return;
    const km = Number(f.length_km.replace(",", "."));
    const gain = Number(f.elevation_gain_m);
    if (km > 0 && gain > 0) set("avg_gradient", ((gain / (km * 1000)) * 100).toFixed(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.length_km, f.elevation_gain_m, gradientTouched]);

  const runGeocoder = async () => {
    if (!f.start_municipality) { toast.error("Escribe primero el municipio de inicio."); return; }
    setGeoBusy(true);
    try {
      const hit = await geocode([f.start_municipality, f.province, "España"].filter(Boolean).join(", "));
      if (!hit) toast.error("No encontrado en Nominatim.");
      else {
        setStartLat(hit.lat); setStartLng(hit.lng); set("start_address", hit.address);
        toast.success("Punto de inicio localizado.");
      }
    } catch { toast.error("Error al geocodificar."); }
    setGeoBusy(false);
  };

  const save = async () => {
    if (!f.name || !f.province) { toast.error("Nombre y provincia son obligatorios."); return; }
    setSaving(true);
    const auto = stravaCategory(num(f.length_km), num(f.avg_gradient));
    const payload: any = {
      name: f.name,
      slope_side: f.slope_side || null,
      province: f.province,
      comarca: f.comarca || null,
      altitude_m: num(f.altitude_m),
      length_km: num(f.length_km),
      elevation_gain_m: num(f.elevation_gain_m),
      avg_gradient: num(f.avg_gradient),
      max_gradient: num(f.max_gradient),
      difficulty: auto.difficulty,
      category: auto.category,
      surface: f.surface,
      profile_url: f.profile_url || null,
      watts_reward: auto.watts_reward,
      is_active: f.is_active,
      start_municipality: f.start_municipality || null,
      start_address: f.start_address || null,
      start_lat: startLat,
      start_lng: startLng,
      is_departure_point: f.is_departure_point,
      lat: num(f.lat),
      lng: num(f.lng),
    };
    const { error } = isEdit
      ? await supabase.from("ports").update(payload).eq("id", port!.id)
      : await supabase.from("ports").insert(payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(isEdit ? "Puerto actualizado" : "Puerto creado");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm md:items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-border bg-[#1a1a1a] p-5 md:max-w-2xl md:rounded-3xl">
        <div className="flex items-start justify-between">
          <h2 className="font-display text-2xl">{isEdit ? "Editar puerto" : "Nuevo puerto"}</h2>
          <button onClick={onClose} className="rounded-full bg-surface-elevated p-1.5"><X className="h-4 w-4" /></button>
        </div>

        <h3 className="mt-5 text-xs font-bold uppercase tracking-wider text-primary">Datos del puerto</h3>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Field label="Nombre"><input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Vertiente"><input className={inputCls} value={f.slope_side} onChange={(e) => set("slope_side", e.target.value)} /></Field>
          <Field label="Provincia"><input className={inputCls} value={f.province} onChange={(e) => set("province", e.target.value)} /></Field>
          <Field label="Comarca"><input className={inputCls} value={f.comarca} onChange={(e) => set("comarca", e.target.value)} /></Field>
          <Field label="Altitud (m)"><input className={inputCls} value={f.altitude_m} onChange={(e) => set("altitude_m", e.target.value)} /></Field>
          <Field label="Longitud (km)"><input className={inputCls} value={f.length_km} onChange={(e) => set("length_km", e.target.value)} /></Field>
          <Field label="Desnivel (m)"><input className={inputCls} value={f.elevation_gain_m} onChange={(e) => set("elevation_gain_m", e.target.value)} /></Field>
          <Field label="Pendiente media (%)">
            <input className={inputCls} value={f.avg_gradient}
              onChange={(e) => { setGradientTouched(true); set("avg_gradient", e.target.value); }} />
          </Field>
          <Field label="Pendiente máxima (%)"><input className={inputCls} value={f.max_gradient} onChange={(e) => set("max_gradient", e.target.value)} /></Field>
          <Field label="Dificultad (1-5)">
            <select className={inputCls} value={f.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          <Field label="Categoría">
            <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Superficie">
            <select className={inputCls} value={f.surface} onChange={(e) => set("surface", e.target.value)}>
              {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Watts"><input className={inputCls} value={f.watts_reward} onChange={(e) => set("watts_reward", e.target.value)} /></Field>
          <Field label="URL del perfil"><input className={inputCls} value={f.profile_url} onChange={(e) => set("profile_url", e.target.value)} /></Field>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Activo
        </label>

        <h3 className="mt-6 text-xs font-bold uppercase tracking-wider text-primary">Punto de inicio</h3>
        <div className="mt-2 space-y-3">
          <div className="flex gap-2">
            <input className={inputCls} value={f.start_municipality} placeholder="Municipio de inicio"
              onChange={(e) => set("start_municipality", e.target.value)} />
            <button onClick={runGeocoder} disabled={geoBusy}
              className="shrink-0 rounded-xl border border-border bg-surface-elevated px-3 text-xs disabled:opacity-50">
              {geoBusy ? "Buscando…" : "Buscar con el geocoder"}
            </button>
          </div>
          <input className={inputCls} value={f.start_address} placeholder="Dirección de inicio"
            onChange={(e) => set("start_address", e.target.value)} />
          <div className="text-xs text-muted-foreground">
            {startLat != null && startLng != null
              ? `${startLat.toFixed(5)}, ${startLng.toFixed(5)} — arrastra el pin para ajustar`
              : "Sin coordenadas de inicio todavía"}
          </div>
          <PinPickerMap
            lat={startLat ?? 41.9}
            lng={startLng ?? 2.0}
            onChange={(la, ln) => { setStartLat(la); setStartLng(ln); }}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_departure_point} onChange={(e) => set("is_departure_point", e.target.checked)} />
            ¿Se puede usar como punto de quedada?
          </label>
        </div>

        <h3 className="mt-6 text-xs font-bold uppercase tracking-wider text-primary">Cima</h3>
        <p className="text-[11px] text-muted-foreground">Coordenadas de la cima (para la ficha del reto)</p>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Field label="Lat cima"><input className={inputCls} value={f.lat} onChange={(e) => set("lat", e.target.value)} /></Field>
          <Field label="Lng cima"><input className={inputCls} value={f.lng} onChange={(e) => set("lng", e.target.value)} /></Field>
        </div>

        <button onClick={save} disabled={saving}
          className="press-scale mt-6 w-full rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50">
          {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear puerto"}
        </button>
      </div>
    </div>
  );
}

function ImportDialog({ onClose, existing }: { onClose: () => void; existing: Port[] }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: number; dup: number; errors: string[] } | null>(null);

  const run = async () => {
    let parsed: any;
    try { parsed = JSON.parse(text); } catch { toast.error("El JSON no es válido."); return; }
    if (!Array.isArray(parsed)) { toast.error("Debe ser un array JSON."); return; }
    setBusy(true);
    const key = (n: string, s: string | null | undefined) => `${(n || "").trim().toLowerCase()}|${(s || "").trim().toLowerCase()}`;
    const seen = new Set(existing.map((e) => key(e.name, e.slope_side)));
    let ok = 0, dup = 0;
    const errors: string[] = [];
    for (const [i, raw] of parsed.entries()) {
      const label = raw?.name ?? `#${i + 1}`;
      const missing = ["name", "province", "length_km", "elevation_gain_m"].filter(
        (k) => raw?.[k] === undefined || raw?.[k] === null || raw?.[k] === "",
      );
      if (missing.length) { errors.push(`${label}: faltan ${missing.join(", ")}`); continue; }
      const k = key(raw.name, raw.slope_side);
      if (seen.has(k)) { dup++; continue; }
      const gain = Number(raw.elevation_gain_m);
      const km = Number(raw.length_km);
      const optionalNumber = (value: unknown) =>
        value == null || (typeof value === "string" && value.trim() === "") ? null : Number(value);
      const apm = optionalNumber(raw.apm_coefficient);
      if (apm !== null && (!Number.isInteger(apm) || apm < 0)) {
        errors.push(`${label}: apm_coefficient debe ser un entero no negativo o estar vacío`);
        continue;
      }
      const avgGradient = raw.avg_gradient ?? Number(((gain / (km * 1000)) * 100).toFixed(1));
      const autoCat = stravaCategory(km, avgGradient);
      const row = {
        ...raw,
        avg_gradient: avgGradient,
        category: autoCat.category,
        difficulty: autoCat.difficulty,
        watts_reward: autoCat.watts_reward,
        surface: raw.surface ?? "asfalto",
        start_municipality: raw.start_municipality || null,
        comarca: raw.comarca || null,
        apm_coefficient: apm,
        start_lat: optionalNumber(raw.start_lat),
        start_lng: optionalNumber(raw.start_lng),
      };
      const { error } = await supabase.from("ports").insert(row);
      if (error) errors.push(`${label}: ${error.message}`);
      else { ok++; seen.add(k); }
    }
    setBusy(false);
    setResult({ ok, dup, errors });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="mx-4 max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-border bg-[#1a1a1a] p-5">
        <div className="flex items-start justify-between">
          <h2 className="font-display text-2xl">Importar puertos</h2>
          <button onClick={onClose} className="rounded-full bg-surface-elevated p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Pega un array JSON. Obligatorios: name, province, length_km, elevation_gain_m.
        </p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
          placeholder='[{"name":"Coll de Pal","province":"Barcelona","length_km":19.1,"elevation_gain_m":1290}]'
          className="mt-3 w-full resize-none rounded-xl border border-border bg-surface px-3 py-2.5 font-mono text-xs" />
        {result && (
          <div className="mt-3 rounded-xl border border-border bg-surface p-3 text-xs">
            <div className="text-emerald-400">{result.ok} insertados</div>
            <div className="text-white/60">{result.dup} duplicados</div>
            <div className="text-red-400">{result.errors.length} errores</div>
            {result.errors.map((e, i) => <div key={i} className="mt-1 text-red-300">· {e}</div>)}
          </div>
        )}
        <button onClick={run} disabled={busy}
          className="press-scale mt-4 w-full rounded-xl bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50">
          {busy ? "Importando…" : "Importar"}
        </button>
      </div>
    </div>
  );
}
